/**
 * RBAC-4.3 · FR-33a. Hand administration over: nominate a successor, they gain it, you lose it, in
 * one transaction (`handover_administration`). Opened from your own record, where Change role is
 * refused (F-136), because this is the one sanctioned way to change your own role.
 *
 * Who may be nominated and which roles you may take are the database's answer
 * (`administration_handover_options`), so the pickers offer exactly what the function would accept.
 * Copy is `lib/personAccess.ts` (tested).
 */
import { useEffect, useState } from 'react';
import { Loader2, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  handoverErrorMessage,
  handoverPreview,
  handoverUnavailableSentence,
  isStaleHandover,
  soleAdministratorSentence,
  type HandoverOptions,
} from '@/lib/personAccess';
import { PersonAccessError, fetchHandoverOptions, handOverAdministration } from '@/services/personAccessService';

export interface HandoverDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onHandedOver: (myRoleId: number) => void;
}

const selectClass =
  'h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

export function HandoverDialog({ open, onOpenChange, onHandedOver }: HandoverDialogProps) {
  const [options, setOptions] = useState<HandoverOptions | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [successorId, setSuccessorId] = useState('');
  const [myRoleId, setMyRoleId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);

  const load = () => {
    setOptions(null);
    setLoadError(null);
    setSuccessorId('');
    setMyRoleId('');
    setError(null);
    setStale(false);
    fetchHandoverOptions()
      .then(setOptions)
      .catch((e: Error) => setLoadError(e.message || 'The handover options could not be read.'));
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const successor = options?.candidates.find((c) => c.userId === successorId) ?? null;
  const myRole = options?.myRoles.find((r) => String(r.id) === myRoleId) ?? null;
  const preview =
    options && successor && myRole
      ? handoverPreview({
          successor,
          adminRoleName: options.myRoleName ?? 'administrator',
          myRoleName: myRole.name,
          youLose: myRole.youLose,
          otherAdministrators: options.otherAdministrators,
        })
      : null;
  const unavailable = options && !options.canHandOver ? handoverUnavailableSentence(options.cannotReason) : null;

  const confirm = async () => {
    if (!options || !successor || !myRole) return;
    setSaving(true);
    setError(null);
    try {
      await handOverAdministration({
        successorId: successor.userId,
        successorRoleId: successor.roleId,
        myRoleId: myRole.id,
        myCurrentRoleId: options.myRoleId,
      });
      onHandedOver(myRole.id);
      onOpenChange(false);
    } catch (e) {
      const rpc = e instanceof PersonAccessError ? e.rpc : { message: e instanceof Error ? e.message : undefined };
      setStale(isStaleHandover(rpc));
      setError(handoverErrorMessage(rpc));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (saving) return;
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[560px]" data-testid="handover-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-primary" aria-hidden />
            {preview ? preview.title : 'Hand over administration'}
          </DialogTitle>
          <DialogDescription>
            Choose who takes over, and the role you keep. Use this when you are leaving or stepping back.
          </DialogDescription>
        </DialogHeader>

        {loadError && (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            {loadError}
          </p>
        )}

        {!options && !loadError && (
          <div className="flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {unavailable && <p className="text-sm text-muted-foreground">{unavailable}</p>}

        {options?.canHandOver && (
          <div className="space-y-4">
            {soleAdministratorSentence(options.otherAdministrators) && (
              <p className="text-sm font-medium" data-testid="handover-sole">
                {soleAdministratorSentence(options.otherAdministrators)}
              </p>
            )}

            {options.candidates.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                There is nobody in this account to hand over to. Add the person who is taking over first.
              </p>
            ) : (
              <>
                <div className="space-y-1.5">
                  <label htmlFor="handover-successor" className="text-sm font-medium">
                    Who takes over
                  </label>
                  {/* Native, not SearchableSelect: that one portals under this dialog (RBAC-4.1). */}
                  <select
                    id="handover-successor"
                    value={successorId}
                    onChange={(e) => {
                      setSuccessorId(e.target.value);
                      setError(null);
                      setStale(false);
                    }}
                    disabled={saving}
                    className={selectClass}
                  >
                    <option value="">Choose a person</option>
                    {options.candidates.map((c) => (
                      <option key={c.userId} value={c.userId}>
                        {[c.firstName, c.lastName].filter(Boolean).join(' ') || 'Unnamed'}
                        {c.roleName ? ` · ${c.roleName}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="handover-my-role" className="text-sm font-medium">
                    The role you keep
                  </label>
                  <select
                    id="handover-my-role"
                    value={myRoleId}
                    onChange={(e) => {
                      setMyRoleId(e.target.value);
                      setError(null);
                      setStale(false);
                    }}
                    disabled={saving}
                    className={selectClass}
                  >
                    <option value="">Choose a role</option>
                    {options.myRoles.map((r) => (
                      <option key={r.id} value={String(r.id)}>
                        {r.isGlobal ? r.name : `${r.name} (your account's own)`}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {preview && (
              <div className="space-y-2 rounded-md border border-border p-3" data-testid="handover-preview">
                <ul className="list-disc space-y-1 pl-5 text-sm">
                  {preview.lines.map((l) => (
                    <li key={l}>{l}</li>
                  ))}
                </ul>
                {preview.soleNote && <p className="text-sm text-muted-foreground">{preview.soleNote}</p>}
              </div>
            )}
          </div>
        )}

        {error && (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          {stale ? (
            <Button onClick={load}>Reload</Button>
          ) : (
            options?.canHandOver && (
              <Button onClick={() => void confirm()} disabled={!preview || saving}>
                {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                {preview ? preview.confirmLabel : 'Hand over'}
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
